import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import {
    FiActivity,
    FiArrowLeft,
    FiBriefcase,
    FiCheckCircle,
    FiEye,
    FiFilter,
    FiRefreshCw,
    FiSearch,
    FiShield,
    FiUser,
    FiX,
    FiXCircle,
} from "react-icons/fi";


import {
    fetchPlatformActivity,
    fetchPlatformActivityFilters,

    setPlatformActivityFilter,
    resetPlatformActivityFilters,

    selectPlatformActivities,
    selectPlatformActivityPagination,
    selectPlatformActivityFilters,
    selectPlatformActivityFilterOptions,
    selectPlatformActivityListLoading,
    selectPlatformActivityFiltersLoading,
} from "../../../../../store/platformActivity/platformActivitySlice";

import "./PlatformActivity.css";


/*
|--------------------------------------------------------------------------
| FORMAT NUMBER
|--------------------------------------------------------------------------
*/

const formatNumber = (
    value
) => {

    return Number(
        value ||
        0
    ).toLocaleString(
        "en-IN"
    );

};


/*
|--------------------------------------------------------------------------
| FORMAT DATE
|--------------------------------------------------------------------------
*/

const formatDate = (
    value
) => {

    if (
        !value
    ) {

        return "-";

    }


    const date =
        new Date(
            value
        );


    if (
        Number.isNaN(
            date.getTime()
        )
    ) {

        return "-";

    }


    return date.toLocaleString(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",

            hour:
                "2-digit",

            minute:
                "2-digit",
        }
    );

};


/*
|--------------------------------------------------------------------------
| FORMAT ACTION
|--------------------------------------------------------------------------
*/

const formatAction = (
    value
) => {

    return String(
        value ||
        ""
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                letter
            ) =>
                letter.toUpperCase()
        );

};


/*
|--------------------------------------------------------------------------
| FORMAT ENTITY TYPE
|--------------------------------------------------------------------------
*/

const formatEntityType = (
    value
) => {

    return String(
        value ||
        ""
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                letter
            ) =>
                letter.toUpperCase()
        );

};


/*
|--------------------------------------------------------------------------
| GET ACTIVITY ID
|--------------------------------------------------------------------------
*/

const getActivityId = (
    activity
) => {

    return (
        activity?._id ||
        activity?.id ||
        null
    );

};


/*
|--------------------------------------------------------------------------
| ACTION ICON
|--------------------------------------------------------------------------
*/

const ActionIcon = ({
    entityType,
}) => {

    const value =
        String(
            entityType ||
            ""
        )
            .trim()
            .toUpperCase();


    if (
        value ===
        "BUSINESS"
    ) {

        return (
            <FiBriefcase />
        );

    }


    if (
        value ===
        "TENANT_USER"
    ) {

        return (
            <FiUser />
        );

    }


    if (
        value ===
        "SUBSCRIPTION"
    ) {

        return (
            <FiActivity />
        );

    }


    return (
        <FiShield />
    );

};


/*
|--------------------------------------------------------------------------
| PLATFORM ACTIVITY LIST
|--------------------------------------------------------------------------
*/

const PlatformActivityList =
    () => {

        const dispatch =
            useDispatch();


        const history =
            useHistory();


        /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

        const activities =
            useSelector(
                selectPlatformActivities
            );


        const pagination =
            useSelector(
                selectPlatformActivityPagination
            );


        const filters =
            useSelector(
                selectPlatformActivityFilters
            );


        const filterOptions =
            useSelector(
                selectPlatformActivityFilterOptions
            );


        const loading =
            useSelector(
                selectPlatformActivityListLoading
            );


        const filtersLoading =
            useSelector(
                selectPlatformActivityFiltersLoading
            );


        /*
        |--------------------------------------------------------------------------
        | SEARCH
        |--------------------------------------------------------------------------
        */

        const [
            searchInput,
            setSearchInput,
        ] =
            useState(
                filters?.search ||
                ""
            );


        /*
        |--------------------------------------------------------------------------
        | LOAD FILTER OPTIONS
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                dispatch(
                    fetchPlatformActivityFilters()
                );

            },
            [
                dispatch,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | LOAD ACTIVITY
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                dispatch(
                    fetchPlatformActivity({
                        page:
                            filters?.page ||
                            1,

                        limit:
                            filters?.limit ||
                            20,

                        search:
                            filters?.search ||
                            "",

                        action:
                            filters?.action ||
                            "",

                        entityType:
                            filters?.entityType ||
                            "",

                        status:
                            filters?.status ||
                            "",

                        tenantId:
                            filters?.tenantId ||
                            "",

                        platformAdminId:
                            filters?.platformAdminId ||
                            "",

                        fromDate:
                            filters?.fromDate ||
                            "",

                        toDate:
                            filters?.toDate ||
                            "",
                    })
                );

            },
            [
                dispatch,
                filters?.page,
                filters?.limit,
                filters?.search,
                filters?.action,
                filters?.entityType,
                filters?.status,
                filters?.tenantId,
                filters?.platformAdminId,
                filters?.fromDate,
                filters?.toDate,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | SYNC SEARCH
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                setSearchInput(
                    filters?.search ||
                    ""
                );

            },
            [
                filters?.search,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | SUMMARY
        |--------------------------------------------------------------------------
        */

        const summary =
            useMemo(
                () => {

                    return activities.reduce(
                        (
                            result,
                            activity
                        ) => {

                            const status =
                                String(
                                    activity?.status ||
                                    ""
                                )
                                    .trim()
                                    .toUpperCase();


                            const entityType =
                                String(
                                    activity?.entityType ||
                                    ""
                                )
                                    .trim()
                                    .toUpperCase();


                            result.total +=
                                1;


                            if (
                                status ===
                                "SUCCESS"
                            ) {

                                result.success +=
                                    1;

                            }


                            if (
                                status ===
                                "FAILED"
                            ) {

                                result.failed +=
                                    1;

                            }


                            if (
                                entityType ===
                                "BUSINESS"
                            ) {

                                result.business +=
                                    1;

                            }


                            if (
                                entityType ===
                                "TENANT_USER"
                            ) {

                                result.users +=
                                    1;

                            }


                            return result;

                        },
                        {
                            total:
                                0,

                            success:
                                0,

                            failed:
                                0,

                            business:
                                0,

                            users:
                                0,
                        }
                    );

                },
                [
                    activities,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | PAGINATION
        |--------------------------------------------------------------------------
        */

        const currentPage =
            Math.max(
                Number(
                    pagination?.page ||
                    filters?.page ||
                    1
                ),
                1
            );


        const totalPages =
            Math.max(
                Number(
                    pagination?.totalPages ||
                    1
                ),
                1
            );


        /*
        |--------------------------------------------------------------------------
        | REFRESH
        |--------------------------------------------------------------------------
        */

        const handleRefresh =
            () => {

                dispatch(
                    fetchPlatformActivity({
                        ...filters,
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | SEARCH
        |--------------------------------------------------------------------------
        */

        const handleSearch =
            (
                event
            ) => {

                event.preventDefault();


                dispatch(
                    setPlatformActivityFilter({
                        key:
                            "page",

                        value:
                            1,
                    })
                );


                dispatch(
                    setPlatformActivityFilter({
                        key:
                            "search",

                        value:
                            searchInput.trim(),
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | CLEAR SEARCH
        |--------------------------------------------------------------------------
        */

        const handleClearSearch =
            () => {

                setSearchInput(
                    ""
                );


                dispatch(
                    setPlatformActivityFilter({
                        key:
                            "page",

                        value:
                            1,
                    })
                );


                dispatch(
                    setPlatformActivityFilter({
                        key:
                            "search",

                        value:
                            "",
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | FILTER CHANGE
        |--------------------------------------------------------------------------
        */

        const handleFilterChange =
            (
                event
            ) => {

                const {
                    name,
                    value,
                } =
                    event.target;


                dispatch(
                    setPlatformActivityFilter({
                        key:
                            "page",

                        value:
                            1,
                    })
                );


                dispatch(
                    setPlatformActivityFilter({
                        key:
                            name,

                        value,
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | RESET FILTERS
        |--------------------------------------------------------------------------
        */

        const handleResetFilters =
            () => {

                setSearchInput(
                    ""
                );


                dispatch(
                    resetPlatformActivityFilters()
                );

            };


        /*
        |--------------------------------------------------------------------------
        | PAGE
        |--------------------------------------------------------------------------
        */

        const handlePageChange =
            (
                page
            ) => {

                const nextPage =
                    Math.min(
                        Math.max(
                            Number(
                                page
                            ) ||
                            1,
                            1
                        ),
                        totalPages
                    );


                if (
                    nextPage ===
                    currentPage
                ) {

                    return;

                }


                dispatch(
                    setPlatformActivityFilter({
                        key:
                            "page",

                        value:
                            nextPage,
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | VIEW DETAILS
        |--------------------------------------------------------------------------
        */

        const handleView =
            (
                activity
            ) => {

                const id =
                    getActivityId(
                        activity
                    );


                if (
                    !id
                ) {

                    return;

                }


                history.push(
                    `/platform/activity/${id}`
                );

            };


        /*
        |--------------------------------------------------------------------------
        | UI
        |--------------------------------------------------------------------------
        */

        return (

                <div className="platform-activity-page">

                    {/*
                    |--------------------------------------------------------------------------
                    | HEADER
                    |--------------------------------------------------------------------------
                    */}

                    <header className="platform-activity-header">

                        <div>

                            <button
                                type="button"

                                className="platform-activity-back"

                                onClick={() =>
                                    history.push(
                                        "/platform/dashboard"
                                    )
                                }
                            >

                                <FiArrowLeft />

                                Dashboard

                            </button>


                            <h1>

                                Platform Activity

                            </h1>


                            <p>

                                Review Super Admin activity, business changes, subscription updates and tenant-user access changes.

                            </p>

                        </div>


                        <button
                            type="button"

                            className="platform-activity-refresh"

                            onClick={
                                handleRefresh
                            }

                            disabled={
                                loading
                            }
                        >

                            <FiRefreshCw
                                className={
                                    loading
                                        ? "platform-activity-spin"
                                        : ""
                                }
                            />

                            Refresh

                        </button>

                    </header>


                    {/*
                    |--------------------------------------------------------------------------
                    | SUMMARY
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-activity-summary">

                        <article>

                            <div>

                                <FiActivity />

                            </div>


                            <span>

                                Total Activity

                            </span>


                            <strong>

                                {
                                    formatNumber(
                                        pagination?.total
                                    )
                                }

                            </strong>

                        </article>


                        <article>

                            <div>

                                <FiCheckCircle />

                            </div>


                            <span>

                                Successful On Page

                            </span>


                            <strong>

                                {
                                    summary.success
                                }

                            </strong>

                        </article>


                        <article>

                            <div>

                                <FiXCircle />

                            </div>


                            <span>

                                Failed On Page

                            </span>


                            <strong>

                                {
                                    summary.failed
                                }

                            </strong>

                        </article>


                        <article>

                            <div>

                                <FiBriefcase />

                            </div>


                            <span>

                                Business Events

                            </span>


                            <strong>

                                {
                                    summary.business
                                }

                            </strong>

                        </article>


                        <article>

                            <div>

                                <FiUser />

                            </div>


                            <span>

                                User Events

                            </span>


                            <strong>

                                {
                                    summary.users
                                }

                            </strong>

                        </article>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | SEARCH
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-activity-toolbar">

                        <form
                            className="platform-activity-search"

                            onSubmit={
                                handleSearch
                            }
                        >

                            <FiSearch />


                            <input
                                type="text"

                                value={
                                    searchInput
                                }

                                onChange={(
                                    event
                                ) =>
                                    setSearchInput(
                                        event
                                            .target
                                            .value
                                    )
                                }

                                placeholder="Search actor, business, user, action or description..."
                            />


                            {searchInput ? (

                                <button
                                    type="button"

                                    className="platform-activity-search-clear"

                                    onClick={
                                        handleClearSearch
                                    }
                                >

                                    <FiX />

                                </button>

                            ) : null}


                            <button
                                type="submit"

                                disabled={
                                    loading
                                }
                            >

                                Search

                            </button>

                        </form>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | FILTERS
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-activity-filters">

                        <div className="platform-activity-filter-title">

                            <FiFilter />

                            <span>

                                Filters

                            </span>

                        </div>


                        <select
                            name="action"

                            value={
                                filters?.action ||
                                ""
                            }

                            onChange={
                                handleFilterChange
                            }

                            disabled={
                                filtersLoading
                            }
                        >

                            <option value="">

                                All Actions

                            </option>


                            {(
                                filterOptions?.actions ||
                                []
                            ).map(
                                (
                                    action
                                ) => (

                                    <option
                                        key={
                                            action
                                        }

                                        value={
                                            action
                                        }
                                    >

                                        {
                                            formatAction(
                                                action
                                            )
                                        }

                                    </option>

                                )
                            )}

                        </select>


                        <select
                            name="entityType"

                            value={
                                filters?.entityType ||
                                ""
                            }

                            onChange={
                                handleFilterChange
                            }

                            disabled={
                                filtersLoading
                            }
                        >

                            <option value="">

                                All Entity Types

                            </option>


                            {(
                                filterOptions?.entityTypes ||
                                []
                            ).map(
                                (
                                    type
                                ) => (

                                    <option
                                        key={
                                            type
                                        }

                                        value={
                                            type
                                        }
                                    >

                                        {
                                            formatEntityType(
                                                type
                                            )
                                        }

                                    </option>

                                )
                            )}

                        </select>


                        <select
                            name="status"

                            value={
                                filters?.status ||
                                ""
                            }

                            onChange={
                                handleFilterChange
                            }
                        >

                            <option value="">

                                All Results

                            </option>

                            <option value="SUCCESS">

                                Success

                            </option>

                            <option value="FAILED">

                                Failed

                            </option>

                        </select>


                        <input
                            type="date"

                            name="fromDate"

                            value={
                                filters?.fromDate ||
                                ""
                            }

                            onChange={
                                handleFilterChange
                            }

                            title="From Date"
                        />


                        <input
                            type="date"

                            name="toDate"

                            value={
                                filters?.toDate ||
                                ""
                            }

                            onChange={
                                handleFilterChange
                            }

                            title="To Date"
                        />


                        <button
                            type="button"

                            className="platform-activity-reset"

                            onClick={
                                handleResetFilters
                            }
                        >

                            <FiX />

                            Reset

                        </button>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | TABLE
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-activity-table-card">

                        <div className="platform-activity-table-wrapper">

                            <table className="platform-activity-table">

                                <thead>

                                    <tr>

                                        <th>
                                            Activity
                                        </th>

                                        <th>
                                            Actor
                                        </th>

                                        <th>
                                            Business
                                        </th>

                                        <th>
                                            Entity
                                        </th>

                                        <th>
                                            Result
                                        </th>

                                        <th>
                                            Date
                                        </th>

                                        <th>
                                            Action
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {loading &&
                                    activities.length ===
                                        0 ? (

                                        <tr>

                                            <td
                                                colSpan={
                                                    7
                                                }
                                            >

                                                <div className="platform-activity-state">

                                                    <div className="platform-activity-loader" />


                                                    <strong>

                                                        Loading platform activity...

                                                    </strong>

                                                </div>

                                            </td>

                                        </tr>

                                    ) : activities.length >
                                      0 ? (

                                        activities.map(
                                            (
                                                activity
                                            ) => {

                                                const activityId =
                                                    getActivityId(
                                                        activity
                                                    );


                                                return (

                                                    <tr
                                                        key={
                                                            activityId
                                                        }
                                                    >

                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | ACTIVITY
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <div className="platform-activity-action-cell">

                                                                <div className="platform-activity-action-icon">

                                                                    <ActionIcon
                                                                        entityType={
                                                                            activity
                                                                                ?.entityType
                                                                        }
                                                                    />

                                                                </div>


                                                                <div>

                                                                    <strong>

                                                                        {
                                                                            formatAction(
                                                                                activity
                                                                                    ?.action
                                                                            )
                                                                        }

                                                                    </strong>


                                                                    <span>

                                                                        {
                                                                            activity
                                                                                ?.description ||
                                                                            "-"
                                                                        }

                                                                    </span>

                                                                </div>

                                                            </div>

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | ACTOR
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <div className="platform-activity-actor">

                                                                <strong>

                                                                    {
                                                                        activity
                                                                            ?.actorName ||
                                                                        activity
                                                                            ?.platformAdminId
                                                                            ?.name ||
                                                                        "Platform Admin"
                                                                    }

                                                                </strong>


                                                                <span>

                                                                    {
                                                                        activity
                                                                            ?.actorEmail ||
                                                                        activity
                                                                            ?.platformAdminId
                                                                            ?.email ||
                                                                        "-"
                                                                    }

                                                                </span>

                                                            </div>

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | BUSINESS
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <div className="platform-activity-business">

                                                                <strong>

                                                                    {
                                                                        activity
                                                                            ?.tenantName ||
                                                                        activity
                                                                            ?.tenantId
                                                                            ?.name ||
                                                                        "-"
                                                                    }

                                                                </strong>


                                                                <span>

                                                                    {
                                                                        activity
                                                                            ?.tenantCode ||
                                                                        activity
                                                                            ?.tenantId
                                                                            ?.code ||
                                                                        "-"
                                                                    }

                                                                </span>

                                                            </div>

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | ENTITY
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <div className="platform-activity-entity">

                                                                <span>

                                                                    {
                                                                        formatEntityType(
                                                                            activity
                                                                                ?.entityType
                                                                        )
                                                                    }

                                                                </span>


                                                                <strong>

                                                                    {
                                                                        activity
                                                                            ?.entityName ||
                                                                        "-"
                                                                    }

                                                                </strong>

                                                            </div>

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | RESULT
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <span
                                                                className={`platform-activity-result ${
                                                                    String(
                                                                        activity
                                                                            ?.status ||
                                                                        ""
                                                                    )
                                                                        .toUpperCase() ===
                                                                    "FAILED"
                                                                        ? "failed"
                                                                        : "success"
                                                                }`}
                                                            >

                                                                {String(
                                                                    activity
                                                                        ?.status ||
                                                                    "SUCCESS"
                                                                )
                                                                    .trim()
                                                                    .toUpperCase() ===
                                                                "FAILED" ? (

                                                                    <FiXCircle />

                                                                ) : (

                                                                    <FiCheckCircle />

                                                                )}


                                                                {
                                                                    activity
                                                                        ?.status ||
                                                                    "SUCCESS"
                                                                }

                                                            </span>

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | DATE
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <span className="platform-activity-date">

                                                                {
                                                                    formatDate(
                                                                        activity
                                                                            ?.createdAt
                                                                    )
                                                                }

                                                            </span>

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | VIEW
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <button
                                                                type="button"

                                                                className="platform-activity-view"

                                                                title="View Activity"

                                                                onClick={() =>
                                                                    handleView(
                                                                        activity
                                                                    )
                                                                }
                                                            >

                                                                <FiEye />

                                                            </button>

                                                        </td>

                                                    </tr>

                                                );

                                            }
                                        )

                                    ) : (

                                        <tr>

                                            <td
                                                colSpan={
                                                    7
                                                }
                                            >

                                                <div className="platform-activity-empty">

                                                    <FiActivity />


                                                    <strong>

                                                        No platform activity found

                                                    </strong>


                                                    <span>

                                                        Activity will appear here when Super Admin actions are performed.

                                                    </span>

                                                </div>

                                            </td>

                                        </tr>

                                    )}

                                </tbody>

                            </table>

                        </div>


                        {/*
                        |--------------------------------------------------------------------------
                        | PAGINATION
                        |--------------------------------------------------------------------------
                        */}

                        <div className="platform-activity-pagination">

                            <span>

                                Page{" "}

                                <strong>

                                    {
                                        currentPage
                                    }

                                </strong>

                                {" "}of{" "}

                                <strong>

                                    {
                                        totalPages
                                    }

                                </strong>

                                {" "}·{" "}

                                {
                                    formatNumber(
                                        pagination?.total
                                    )
                                }

                                {" "}activities

                            </span>


                            <div>

                                <button
                                    type="button"

                                    disabled={
                                        loading ||
                                        currentPage <=
                                            1
                                    }

                                    onClick={() =>
                                        handlePageChange(
                                            currentPage -
                                            1
                                        )
                                    }
                                >

                                    Previous

                                </button>


                                <span>

                                    {
                                        currentPage
                                    }

                                </span>


                                <button
                                    type="button"

                                    disabled={
                                        loading ||
                                        currentPage >=
                                            totalPages
                                    }

                                    onClick={() =>
                                        handlePageChange(
                                            currentPage +
                                            1
                                        )
                                    }
                                >

                                    Next

                                </button>

                            </div>

                        </div>

                    </section>

                </div>

        );

    };


export default PlatformActivityList;